import { lazy, memo, Suspense, useCallback, useState, useContext } from "react";

import {
  Box,
  Collapse,
  IconButton,
  TableCell,
  TableRow,
  Typography,
  useTheme,
  Chip,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import PropTypes from "prop-types";

import { ButtonV1 } from "components";
import FGRefreshContext from "../../FGRefreshContext";
import { SoOrderInfoTooltip, SoStatusChip } from "../components";
import { CLOSURE_TYPE, PAYER_CODE_LABELS, SO_BOARD_LABELS } from "../constants";
import { FGStatusChip } from "pages/FGTracking/components";
import { parseMtValue, getInvoiceStatusInfo,getPayerCodeChipStyles } from "../makeData";
import { useToaster } from "hooks";
import { moneyFormat } from "utils";
import { success, error } from "constants";
import { postCall_v2 } from "services";

const MARK_CLOSED_API_ENDPOINT =
  "/joms/api/internal/v1/plant-supply/update-status";

const SoLineItemTable = lazy(() => import("./SoLineItemTable"));
const CloseSOModal = lazy(() => import("./SoMarkCloseModal"));
const SORemapModal = lazy(() => import("./SoRemapModal"));

const SoTrackingTableRow = ({
  order,
  isExpanded,
  onToggleExpand,
  columnLength,
  activeTab,
}) => {
  const theme = useTheme();
  const triggerToaster = useToaster();
  const refreshOrders = useContext(FGRefreshContext);
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [isRemapModalOpen, setIsRemapModalOpen] = useState(false);
  const handleExpandToggle = useCallback(() => {
    onToggleExpand(order.orderId);
  }, [onToggleExpand, order.orderId]);

  const sourceSellerName =
    order.sellerInfo?.sourceSeller?.sellerDisplayName ||
    order.sellerInfo?.sourceSeller?.name ||
    order.sellerName;

  const handleOpenCloseModal = useCallback(() => {
    setIsCloseModalOpen(true);
  }, []);

  const handleCloseModalClose = useCallback(() => {
    setIsCloseModalOpen(false);
  }, []);

  const handleCloseModalSubmit = useCallback(
    async (payload) => {
      try {
        let res = await postCall_v2(MARK_CLOSED_API_ENDPOINT, payload);
        if (res?.data?.success) {
          triggerToaster(
            `SO ${order.soNumber} closed - moved to Historical SOs`,
            success,
          );
          refreshOrders?.();
        }
      } catch {
        triggerToaster(
          `Something went wrong - Unable to close ${order.soNumber}`,
          error,
        );
      } finally {
        setIsCloseModalOpen(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [order.orderId],
  );

  const handleOpenRemapModal = useCallback(() => {
    setIsRemapModalOpen(true);
  }, []);

  const handleRemapModalClose = useCallback(() => {
    setIsRemapModalOpen(false);
  }, []);

  const handleRemapModalSubmit = useCallback(
    (payload) => {
      //remap SO to active buyer order api call
      triggerToaster(
        `SO ${order.soNumber} remapped to ${payload.newSloCode} - terminal mapping archived`,
        success,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [order.soNumber],
  );

  const renderInvoiceStatusChip = () => {
    const { label, bgColor, textColor } = getInvoiceStatusInfo(
      parseMtValue(order?.invoicedQty),
      parseMtValue(order.totalQty),
      theme,
    );

    return (
      <SoStatusChip
        label={label}
        bgColor={bgColor}
        textColor={textColor}
        showDot
      />
    );
  };

  const renderActionCell = useCallback(() => {
    if (order?.status === "CLOSED") {
      return (
        <SoStatusChip
          label={`Closed · ${CLOSURE_TYPE[order.closureType] || "-"}`}
          bgColor="grey.200"
          textColor="text.secondary"
          fontWeight={600}
          height={26}
        />
      );
    }

    return (
      <Box sx={{ display: "flex" }}>
        <ButtonV1
          title="Mark closed"
          variant="outlined"
          size="small"
          style={{
            minWidth: 72,
            height: 26,
            marginLeft: 2,
          }}
          textStyle={{ fontSize: 12 }}
          onClick={handleOpenCloseModal}
        />
        {/* {order?.orderStatus && activeTab === "TERMINAL_ORDERS" && (
          <ButtonV1
            title="Remap"
            variant="outlined"
            size="small"
            style={{
              minWidth: 62,
              height: 26,
              marginLeft: 2,
              backgroundColor: theme.palette.orange.light,
              color: theme.palette.orange.dark,
              border: `1px solid ${theme.palette.orange.dark}`,
              "&:hover": {
                backgroundColor: theme.palette.orange.light,
                borderColor: theme.palette.orange.dark,
              },
            }}
            textStyle={{ fontSize: 12 }}
            onClick={handleOpenRemapModal}
          />
        )} */}
      </Box>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.status, order?.orderStatus, order.closureType]);
  return (
    <>
      <TableRow hover>
        <TableCell width={40}>
          <IconButton size="small" onClick={handleExpandToggle}>
            {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          <SoStatusChip
            label={PAYER_CODE_LABELS[order.payerCode] || "-"}
            fontWeight={700}
            {...getPayerCodeChipStyles(order.payerCode, theme)}
          />
        </TableCell>
        <TableCell>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
            {sourceSellerName}
          </Typography>
        </TableCell>
        <TableCell sx={{ color: theme.palette.text.secondary }}>
          {order.vendorLocation}
        </TableCell>
        <TableCell>
          <Typography
            color={theme.palette.primary.main}
            sx={{ fontWeight: 500 }}
          >
            {order.soNumber}
          </Typography>
        </TableCell>
        <TableCell>
          {order?.rawSoListingStatus === SO_BOARD_LABELS.mapped ? (
            <SoOrderInfoTooltip
              orderInfo={{
                orderNo: order?.sellerOrder,
                buyerName: order?.buyerName,
                sellerName: order?.sellerName,
                buyerGstin: order?.buyerGstin,
                sellerGstin: order?.sellerGstin,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "nowrap",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "13px",
                    color: theme.palette.primary.main,
                    textDecoration: "underline dotted",
                    textUnderlineOffset: "4px",
                  }}
                >
                  {order.sellerOrder}
                </Typography>

                {order?.orderStatus && activeTab === "TERMINAL_ORDERS" && (
                  <Chip
                    label={
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: theme.palette.error.dark,
                        }}
                      >
                        {order?.orderStatus}
                      </Typography>
                    }
                    size="small"
                    sx={{
                      height: 20,
                      borderRadius: "12px",
                      bgcolor: theme.palette.error.errorBgColor,
                      border: `1px solid ${theme.palette.error.dark}`,
                      "& .MuiChip-label": { px: "10px" },
                    }}
                  />
                )}
              </Box>
            </SoOrderInfoTooltip>
          ) : (
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 13,
                color: theme.palette.text.secondary,
                whiteSpace: "nowrap",
              }}
            >
              - Unmapped
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <SoStatusChip
            label={order?.soType}
            bgColor="grey.200"
            textColor="text.secondary"
            fontWeight={600}
            height={20}
          />
        </TableCell>
        <TableCell>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
            {order.buyerName}
          </Typography>
        </TableCell>
        <TableCell sx={{ color: theme.palette.text.secondary }}>
          {order.shipTo}
        </TableCell>
        <TableCell>{parseMtValue(order.totalQty)}</TableCell>
        <TableCell>{parseMtValue(order?.invoicedQty)}</TableCell>
        <TableCell>
          <FGStatusChip label={order.fgReady} tone={order.fgReadyTone} />
        </TableCell>
        <TableCell>{parseMtValue(order?.fgAwaited)}</TableCell>
        <TableCell>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "nowrap",
              gap: 1,
            }}
          >
            {renderInvoiceStatusChip()}
            {order?.status === "ABANDONED" && (
              <SoStatusChip
                label="ABANDONED"
                bgColor={theme.palette.orange.light}
                textColor={theme.palette.orange.dark}
              />
            )}
          </Box>
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>
          {moneyFormat(order.netLandedValue, 0)}
        </TableCell>
        <TableCell>
          <SoStatusChip
            label={order.paymentMode}
            bgColor="grey.100"
            textColor="text.secondary"
            fontWeight={600}
            height={20}
            border={`1px solid ${theme.palette.text.secondary}`}
          />
        </TableCell>
        <TableCell>{renderActionCell()}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0 }}
          colSpan={columnLength}
        >
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box py={1.5}>
              {order.lineItems.length > 0 ? (
                <Suspense fallback={null}>
                  <SoLineItemTable order={order} />
                </Suspense>
              ) : (
                <Box
                  p={2}
                  border="1px solid"
                  borderColor="grey.300"
                  borderRadius={1.5}
                >
                  <Typography variant="body2" color="text.secondary">
                    {SO_BOARD_LABELS.noLineItems}
                  </Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      {isCloseModalOpen && (
        <Suspense fallback={null}>
          <CloseSOModal
            open={isCloseModalOpen}
            onClose={handleCloseModalClose}
            onSubmit={handleCloseModalSubmit}
            soNumber={order.sellerOrder}
            order={order}
          />
        </Suspense>
      )}

      {isRemapModalOpen && (
        <Suspense fallback={null}>
          <SORemapModal
            open={isRemapModalOpen}
            onClose={handleRemapModalClose}
            onSubmit={handleRemapModalSubmit}
            order={order}
          />
        </Suspense>
      )}
    </>
  );
};

SoTrackingTableRow.propTypes = {
  order: PropTypes.shape({
    orderId: PropTypes.string,
    payerCode: PropTypes.string,
    sellerName: PropTypes.string,
    vendorLocation: PropTypes.string,
    soNumber: PropTypes.string,
    sellerOrder: PropTypes.string,
    soType: PropTypes.string,
    buyerName: PropTypes.string,
    shipTo: PropTypes.string,
    totalQty: PropTypes.string,
    invoicedQty: PropTypes.string,
    fgReady: PropTypes.string,
    fgReadyTone: PropTypes.string,
    fgAwaited: PropTypes.string,
    netLandedValue: PropTypes.string,
    paymentMode: PropTypes.string,
    closureType: PropTypes.string,
    buyerGstin: PropTypes.string,
    sellerGstin: PropTypes.string,
    rawSoListingStatus: PropTypes.string,
    lineItems: PropTypes.array.isRequired,
  }).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  columnLength: PropTypes.number.isRequired,
  activeTab: PropTypes.string,
};

export default memo(SoTrackingTableRow);
